<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { useCarBookingStore } from 'dashboard/stores/carBooking';
import CarBookingAPI from 'dashboard/api/carBooking';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import DatePicker from 'dashboard/components/carBooking/DatePicker.vue';
import TimePicker from 'dashboard/components/carBooking/TimePicker.vue';
import { useStationSchedule } from 'dashboard/components/carBooking/useStationSchedule';
import countries from 'shared/constants/countries';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const props = defineProps({
  conversationId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const carBooking = useCarBookingStore();
const currentChat = useMapGetter('getSelectedChat');

const session = computed(() => carBooking.sessionFor(props.conversationId));
const form = computed(() => session.value.form);
const uiFlags = computed(() => carBooking.uiFlags);

const ratePlans = computed(() => session.value.ratePlans || []);
const planLoading = ref(false);
const planError = ref('');

const availableProviders = computed(() =>
  carBooking.availableProviders(props.conversationId)
);
const selectedProviders = computed(() => session.value.providers || []);
const planByProvider = computed(() => session.value.planByProvider || {});

const isProviderSelected = provider =>
  selectedProviders.value.includes(provider);

const toggleProvider = provider =>
  carBooking.toggleProvider(props.conversationId, provider);

const allSelected = computed(
  () =>
    availableProviders.value.length > 0 &&
    selectedProviders.value.length === availableProviders.value.length
);

// "All" is a toggle: once everything is on, pressing it again clears the selection
// rather than being a no-op the agent has to undo one provider at a time.
const toggleAllProviders = () =>
  carBooking.setProviders(
    props.conversationId,
    allSelected.value ? [] : availableProviders.value
  );

const planOptionsFor = provider =>
  carBooking
    .plansForProvider(props.conversationId, provider)
    .map(plan => ({ value: plan.slug, label: plan.label }));

const selectPlan = (provider, slug) =>
  carBooking.setPlanForProvider(props.conversationId, provider, slug);

const { pickupAvailability, dropoffAvailability, rentalDays } =
  useStationSchedule(props.conversationId);

const locations = ref([]);
const activeField = ref(null);

// When true, the return place mirrors the pickup place and only one location
// cell is shown. Default to "same place" — the common car-rental case.
const samePlace = ref(true);

const update = patch => carBooking.setForm(props.conversationId, patch);

// Copies the current pickup location onto the dropoff fields and warms the
// dropoff schedule cache (same code resolves pickup/dropoff availability).
const mirrorPickupToDropoff = () => {
  if (!form.value.pickupLocationCode) return;
  update({
    dropoffLocationCode: form.value.pickupLocationCode,
    dropoffLocationName: form.value.pickupLocationName,
  });
  carBooking.fetchSchedule(props.conversationId, form.value.pickupLocationCode);
};

// Flipping ON immediately mirrors pickup → dropoff so canSearch stays
// satisfiable; flipping OFF leaves the dropoff as-is for the agent to edit.
const toggleSamePlace = () => {
  samePlace.value = !samePlace.value;
  if (samePlace.value) mirrorPickupToDropoff();
};

const countryOptions = countries.map(country => ({
  value: country.id,
  label: `${country.emoji} ${country.name}`,
}));

// Today's date as a local "YYYY-MM-DD" string (no UTC round-trip).
const todayStr = computed(() => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
});

const fetchLocations = debounce(async () => {
  const query =
    activeField.value === 'pickup'
      ? form.value.pickupLocationName
      : form.value.dropoffLocationName;
  if (!query || query.length < 2) {
    locations.value = [];
    return;
  }
  const { data } = await CarBookingAPI.locations(props.conversationId, query);
  locations.value = Array.isArray(data) ? data : data?.locations || [];
}, 350);

// Station name, IATA/station code, city and postcode all go through the same
// box; the gateway matches on all of them and returns hits ranked by relevance.
const filteredLocations = computed(() => locations.value.slice(0, 8));

const stationSubtitle = loc =>
  [loc.address?.city, loc.country].filter(Boolean).join(', ');

const onLocationInput = field => {
  activeField.value = field;
  fetchLocations();
};

const selectLocation = (field, loc) => {
  if (field === 'pickup') {
    update({ pickupLocationCode: loc.code, pickupLocationName: loc.name });
  } else {
    update({ dropoffLocationCode: loc.code, dropoffLocationName: loc.name });
  }
  activeField.value = null;
  // Fire-and-forget: warms the schedule cache used for time-window gating.
  carBooking.fetchSchedule(props.conversationId, loc.code);
  // In same-place mode the return mirrors the pickup as it changes.
  if (field === 'pickup' && samePlace.value) {
    update({ dropoffLocationCode: loc.code, dropoffLocationName: loc.name });
  }
};

const prefill = () => carBooking.prefill(props.conversationId);

const canSearch = computed(
  () =>
    form.value.pickupLocationCode &&
    form.value.dropoffLocationCode &&
    form.value.pickupDate &&
    form.value.pickupTime &&
    form.value.dropoffDate &&
    form.value.dropoffTime &&
    form.value.driverAge &&
    form.value.countryOfResidence &&
    selectedProviders.value.length > 0
);

const search = async () => {
  try {
    await carBooking.searchFromForm(props.conversationId);
  } catch (error) {
    // Surface the gateway's reason (e.g. "pick-up station is closed at the
    // selected time" for an after-hours pickup) instead of failing silently.
    const data = error.response?.data;
    if (data?.code === 'provider.rate_plan_ambiguous') {
      useAlert(
        t('CONVERSATION.CAR_BOOKING.PLAN.AMBIGUOUS', {
          slugs: (data.extensions?.available || []).join(', '),
        })
      );
      return;
    }
    useAlert(
      data?.detail || t('CONVERSATION.CAR_BOOKING.TRIP.NO_AVAILABILITY')
    );
  }
};

onMounted(() => {
  const contact = currentChat.value?.meta?.sender || {};
  const patch = {};
  if (
    !form.value.countryOfResidence &&
    contact.additional_attributes?.country_code
  ) {
    patch.countryOfResidence = contact.additional_attributes.country_code;
  }
  if (!form.value.driverName && contact.name) patch.driverName = contact.name;
  if (!form.value.email && contact.email) patch.email = contact.email;
  if (Object.keys(patch).length) update(patch);
});

onMounted(async () => {
  planLoading.value = true;
  planError.value = '';
  try {
    await carBooking.fetchRatePlans(props.conversationId);
    // Start with every provider selected on their default plan so the agent can
    // search immediately and sees the widest set of offers; they can narrow it down
    // or change any provider's rate type before searching.
    if (availableProviders.value.length && !selectedProviders.value.length) {
      carBooking.setProviders(props.conversationId, availableProviders.value);
    }
  } catch (e) {
    const data = e.response?.data;
    if (data?.code === 'provider.rate_plan_ambiguous') {
      planError.value = t('CONVERSATION.CAR_BOOKING.PLAN.AMBIGUOUS', {
        slugs: (data.extensions?.available || []).join(', '),
      });
    } else {
      planError.value =
        data?.detail || t('CONVERSATION.CAR_BOOKING.PLAN.ERROR');
    }
  } finally {
    planLoading.value = false;
  }
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- providers to search, each with its own rate plan -->
    <div class="flex flex-col gap-2">
      <div class="flex flex-wrap items-center gap-2">
        <span
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CONVERSATION.CAR_BOOKING.PLAN.TITLE') }}
        </span>
        <span v-if="planLoading" class="text-xs text-n-slate-11">
          {{ t('CONVERSATION.CAR_BOOKING.PLAN.LOADING') }}
        </span>
        <span v-else-if="planError" class="text-xs text-n-ruby-11">
          {{ planError }}
        </span>
        <span
          v-else-if="!availableProviders.length"
          class="text-xs text-n-slate-11"
        >
          {{ t('CONVERSATION.CAR_BOOKING.PLAN.EMPTY') }}
        </span>
        <button
          v-else
          type="button"
          class="rounded-md border border-n-weak px-2 py-1 text-xs font-semibold transition-colors"
          :class="
            allSelected
              ? 'bg-n-brand text-white border-n-brand'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="toggleAllProviders"
        >
          {{ t('CONVERSATION.CAR_BOOKING.PLAN.ALL_PROVIDERS') }}
        </button>
      </div>

      <!-- providers sit side by side, each with its rate plan stacked underneath.
           A dropdown rather than a button per plan so a provider's width stays
           fixed however many contracts it exposes. -->
      <div v-if="ratePlans.length" class="flex flex-wrap items-start gap-2">
        <div
          v-for="provider in availableProviders"
          :key="provider"
          class="flex flex-col items-start gap-2 rounded-lg border bg-n-surface-1 px-3 py-2 transition-colors"
          :class="
            isProviderSelected(provider) ? 'border-n-brand' : 'border-n-weak'
          "
        >
          <button
            type="button"
            class="flex items-center gap-2 text-xs font-semibold capitalize"
            :class="
              isProviderSelected(provider)
                ? 'text-n-slate-12'
                : 'text-n-slate-10'
            "
            @click="toggleProvider(provider)"
          >
            <span
              class="size-4 rounded border"
              :class="
                isProviderSelected(provider)
                  ? 'i-lucide-check bg-n-brand border-n-brand text-white'
                  : 'border-n-slate-6'
              "
            />
            {{ provider }}
          </button>

          <Select
            :model-value="planByProvider[provider]"
            :options="planOptionsFor(provider)"
            :disabled="!isProviderSelected(provider)"
            class="!w-44"
            @update:model-value="selectPlan(provider, $event)"
          />
        </div>
      </div>
    </div>

    <!-- control row -->
    <div class="flex flex-wrap items-center gap-3">
      <button
        type="button"
        class="inline-flex items-center gap-2 rounded-lg border border-n-weak bg-n-surface-1 px-3 py-2 text-sm font-semibold text-n-slate-12 transition-colors hover:border-n-slate-6"
        @click="toggleSamePlace"
      >
        <span class="i-lucide-arrow-left-right text-base text-n-slate-10" />
        {{ t('CONVERSATION.CAR_BOOKING.TRIP.SAME_PLACE') }}
        <span
          class="text-base text-n-slate-10"
          :class="samePlace ? 'i-lucide-check' : 'i-lucide-chevron-down'"
        />
      </button>

      <span class="flex-1" />

      <NextButton
        faded
        blue
        sm
        icon="i-lucide-sparkles"
        :label="t('CONVERSATION.CAR_BOOKING.TRIP.PREFILL')"
        :is-loading="uiFlags.prefilling"
        @click="prefill"
      />

      <div class="flex flex-col gap-1">
        <label
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.DRIVER_AGE') }}
        </label>
        <NextInput
          class="w-24"
          size="sm"
          :model-value="form.driverAge"
          type="number"
          :placeholder="
            t('CONVERSATION.CAR_BOOKING.TRIP.DRIVER_AGE_PLACEHOLDER')
          "
          @update:model-value="update({ driverAge: $event })"
        />
      </div>

      <div class="flex w-48 flex-col gap-1 min-w-0">
        <label
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.COUNTRY') }}
        </label>
        <ComboBox
          :model-value="form.countryOfResidence"
          :options="countryOptions"
          :placeholder="t('CONVERSATION.CAR_BOOKING.TRIP.COUNTRY_PLACEHOLDER')"
          :search-placeholder="
            t('CONVERSATION.CAR_BOOKING.TRIP.COUNTRY_SEARCH')
          "
          :empty-state="t('CONVERSATION.CAR_BOOKING.TRIP.COUNTRY_EMPTY')"
          @update:model-value="update({ countryOfResidence: $event })"
        />
      </div>
    </div>

    <!-- hero search bar -->
    <!-- NB: no overflow-hidden here — the location dropdown and the date/time
    popovers render absolutely inside the cells and must escape this row. -->
    <div
      class="flex items-stretch rounded-xl border border-n-strong bg-n-surface-1"
    >
      <!-- pickup (and return) location -->
      <div
        class="relative flex flex-[1.4] flex-col justify-center gap-1 border-r border-n-weak px-4 py-3 min-w-[160px]"
      >
        <span
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{
            samePlace
              ? t('CONVERSATION.CAR_BOOKING.TRIP.PICKUP_RETURN_LOCATION')
              : t('CONVERSATION.CAR_BOOKING.TRIP.PICKUP_LOCATION')
          }}
        </span>
        <div class="flex items-center gap-2">
          <span class="i-lucide-map-pin shrink-0 text-base text-n-slate-10" />
          <input
            :value="form.pickupLocationName"
            :placeholder="
              t('CONVERSATION.CAR_BOOKING.TRIP.PICKUP_LOCATION_PLACEHOLDER')
            "
            class="w-full border-none bg-transparent p-0 text-sm font-semibold text-n-slate-12 outline-none placeholder:font-normal placeholder:text-n-slate-9"
            @input="
              update({
                pickupLocationName: $event.target.value,
                pickupLocationCode: '',
              });
              onLocationInput('pickup');
            "
            @focus="onLocationInput('pickup')"
          />
        </div>
        <ul
          v-if="activeField === 'pickup' && filteredLocations.length"
          class="absolute top-full left-0 z-10 mt-1.5 w-[300px] overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-lg"
        >
          <li
            v-for="loc in filteredLocations"
            :key="loc.code"
            class="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-1"
            @click="selectLocation('pickup', loc)"
          >
            <span
              class="shrink-0 text-base text-n-slate-10"
              :class="
                loc.type === 'AIRPORT' ? 'i-lucide-plane' : 'i-lucide-map-pin'
              "
            />
            <span class="flex min-w-0 flex-col">
              <span class="truncate">{{ loc.name }}</span>
              <span class="truncate text-xs text-n-slate-10">
                {{ stationSubtitle(loc) }}
              </span>
            </span>
            <span class="ml-auto shrink-0 text-xs text-n-slate-10">
              {{ loc.code }}
            </span>
          </li>
        </ul>
      </div>

      <!-- return location (only when different) -->
      <div
        v-if="!samePlace"
        class="relative flex flex-[1.4] flex-col justify-center gap-1 border-r border-n-weak px-4 py-3 min-w-[160px]"
      >
        <span
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.RETURN_LOCATION') }}
        </span>
        <div class="flex items-center gap-2">
          <span class="i-lucide-map-pin shrink-0 text-base text-n-slate-10" />
          <input
            :value="form.dropoffLocationName"
            :placeholder="
              t('CONVERSATION.CAR_BOOKING.TRIP.DROPOFF_LOCATION_PLACEHOLDER')
            "
            class="w-full border-none bg-transparent p-0 text-sm font-semibold text-n-slate-12 outline-none placeholder:font-normal placeholder:text-n-slate-9"
            @input="
              update({
                dropoffLocationName: $event.target.value,
                dropoffLocationCode: '',
              });
              onLocationInput('dropoff');
            "
            @focus="onLocationInput('dropoff')"
          />
        </div>
        <ul
          v-if="activeField === 'dropoff' && filteredLocations.length"
          class="absolute top-full left-0 z-10 mt-1.5 w-[300px] overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 shadow-lg"
        >
          <li
            v-for="loc in filteredLocations"
            :key="loc.code"
            class="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-n-slate-12 hover:bg-n-alpha-1"
            @click="selectLocation('dropoff', loc)"
          >
            <span
              class="shrink-0 text-base text-n-slate-10"
              :class="
                loc.type === 'AIRPORT' ? 'i-lucide-plane' : 'i-lucide-map-pin'
              "
            />
            <span class="flex min-w-0 flex-col">
              <span class="truncate">{{ loc.name }}</span>
              <span class="truncate text-xs text-n-slate-10">
                {{ stationSubtitle(loc) }}
              </span>
            </span>
            <span class="ml-auto shrink-0 text-xs text-n-slate-10">
              {{ loc.code }}
            </span>
          </li>
        </ul>
      </div>

      <!-- rental length: both dates + days pill -->
      <div
        class="flex flex-[1.5] flex-col justify-center gap-1 border-r border-n-weak px-4 py-3 min-w-[250px]"
      >
        <span
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.RENTAL_LENGTH') }}
        </span>
        <div class="flex items-center gap-1.5 whitespace-nowrap">
          <DatePicker
            compact
            :model-value="form.pickupDate"
            :min="todayStr"
            trigger-class="px-0 text-sm font-semibold"
            @update:model-value="update({ pickupDate: $event })"
          />
          <span class="i-lucide-arrow-right shrink-0 text-sm text-n-slate-9" />
          <DatePicker
            compact
            :model-value="form.dropoffDate"
            :min="form.pickupDate || todayStr"
            trigger-class="px-0 text-sm font-semibold"
            @update:model-value="update({ dropoffDate: $event })"
          />
          <span
            v-if="rentalDays"
            class="ml-0.5 shrink-0 rounded-full bg-n-brand/10 px-2 py-0.5 text-[11px] font-semibold text-n-brand"
          >
            {{
              t('CONVERSATION.CAR_BOOKING.TRIP.RENTAL_DAYS', {
                count: rentalDays,
              })
            }}
          </span>
        </div>
      </div>

      <!-- pickup time -->
      <div
        class="flex w-32 flex-col justify-center gap-1 border-r border-n-weak px-4 py-3"
      >
        <span
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.PICKUP_TIME') }}
        </span>
        <TimePicker
          :model-value="form.pickupTime"
          :min-time="pickupAvailability.minTime"
          :max-time="pickupAvailability.maxTime"
          :allow-outside="pickupAvailability.allowOutside"
          :closed="pickupAvailability.closed"
          trigger-class="px-0 text-sm font-semibold"
          @update:model-value="update({ pickupTime: $event })"
        />
      </div>

      <!-- dropoff time -->
      <div
        class="flex w-32 flex-col justify-center gap-1 border-r border-n-weak px-4 py-3"
      >
        <span
          class="text-[10px] font-semibold uppercase tracking-wide text-n-slate-10"
        >
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.DROPOFF_TIME') }}
        </span>
        <TimePicker
          :model-value="form.dropoffTime"
          :min-time="dropoffAvailability.minTime"
          :max-time="dropoffAvailability.maxTime"
          :allow-outside="dropoffAvailability.allowOutside"
          :closed="dropoffAvailability.closed"
          trigger-class="px-0 text-sm font-semibold"
          @update:model-value="update({ dropoffTime: $event })"
        />
      </div>

      <!-- search -->
      <button
        type="button"
        :disabled="!canSearch || uiFlags.searching"
        class="flex w-16 shrink-0 items-center justify-center rounded-r-xl bg-n-brand text-white transition-[filter] hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-50"
        @click="search"
      >
        <span
          class="size-5"
          :class="
            uiFlags.searching
              ? 'i-lucide-loader-circle animate-spin'
              : 'i-lucide-search'
          "
        />
      </button>
    </div>

    <!-- hints -->
    <div class="flex flex-col gap-1">
      <p v-if="pickupAvailability.closed" class="text-xs text-n-slate-10">
        {{ t('CONVERSATION.CAR_BOOKING.TRIP.CLOSED_DAY') }}
      </p>
      <p
        v-else-if="pickupAvailability.minTime && pickupAvailability.maxTime"
        class="text-xs text-n-green-11"
      >
        {{
          t('CONVERSATION.CAR_BOOKING.TRIP.OPEN_HOURS', {
            opens: pickupAvailability.minTime,
            closes: pickupAvailability.maxTime,
          })
        }}
      </p>

      <p v-if="dropoffAvailability.closed" class="text-xs text-n-slate-10">
        {{ t('CONVERSATION.CAR_BOOKING.TRIP.CLOSED_DAY') }}
      </p>
      <template
        v-else-if="dropoffAvailability.minTime && dropoffAvailability.maxTime"
      >
        <p class="text-xs text-n-green-11">
          {{
            t('CONVERSATION.CAR_BOOKING.TRIP.OPEN_HOURS', {
              opens: dropoffAvailability.minTime,
              closes: dropoffAvailability.maxTime,
            })
          }}
        </p>
        <p
          v-if="dropoffAvailability.afterHoursStatus === 'R'"
          class="text-xs text-n-slate-10"
        >
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.AFTER_HOURS_REQUEST') }}
        </p>
        <p
          v-else-if="dropoffAvailability.afterHoursStatus === 'F'"
          class="text-xs text-n-slate-10"
        >
          {{ t('CONVERSATION.CAR_BOOKING.TRIP.AFTER_HOURS') }}
        </p>
      </template>
    </div>
  </div>
</template>
